import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { ChartCard } from "@/components/ReportCharts";
import {
  FileText, CheckCircle, FolderOpen, Stack, Globe, ShieldCheck, FilePlus, ArrowRight, Sparkle,
  TrendUp, TrendDown, Newspaper, ArrowSquareOut,
} from "@phosphor-icons/react";

const KPIS = [
  { key: "total_reports", label: "Total Reports", icon: FileText, color: "#004182", bg: "#e6f0fb" },
  { key: "completed_reports", label: "Completed", icon: CheckCircle, color: "#15803d", bg: "#e7f6ec" },
  { key: "total_documents", label: "Documents", icon: FolderOpen, color: "#b45309", bg: "#fef3e2" },
  { key: "total_templates", label: "Templates", icon: Stack, color: "#7c3aed", bg: "#f3e8ff" },
  { key: "url_sources", label: "URL Sources", icon: Globe, color: "#be185d", bg: "#fce7f0" },
  { key: "audit_events", label: "Audit Events", icon: ShieldCheck, color: "#0e7490", bg: "#e0f2f6" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [share, setShare] = useState(null);
  const [news, setNews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/market/gail-share").then((r) => setShare(r.data)).catch(() => {});
    api.get("/market/gail-news?limit=6").then((r) => setNews(r.data)).catch(() => {});
  }, []);

  const up = share?.change != null && share.change >= 0;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1">
            <Sparkle size={14} weight="fill" /> Welcome back
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 tracking-tight">
            Hello, <span className="text-[#004182]">{(stats && (stats.recent_reports?.[0]?.created_by)) || "CMD"}</span>
          </h1>
          <p className="text-slate-500 text-base mt-2">Generate or update CMD reports using templates and reference documents.</p>
        </div>
        <Button data-testid="dashboard-generate-btn" onClick={() => navigate("/generate")}
          className="gold-btn rounded-2xl h-14 px-7 font-semibold text-base gap-2 shadow-lg shadow-amber-500/20">
          <FilePlus size={22} weight="bold" /> Generate New Report
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {KPIS.map((k, i) => (
          <div key={k.key} data-testid={`kpi-${k.key}`}
            className="card card-hover p-5 fade-up" style={{ animationDelay: `${i * 55}ms` }}>
            <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: k.bg }}>
              <k.icon size={22} weight="duotone" style={{ color: k.color }} />
            </div>
            <div className="kpi-value text-4xl font-bold text-slate-900 mt-4">{stats ? stats[k.key] : "—"}</div>
            <div className="text-sm text-slate-500 mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Live market row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5" data-testid="dashboard-share-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-900 font-semibold">
              <TrendUp size={18} weight="duotone" className="text-[#004182]" /> GAIL Share Price <span className="text-[11px] text-slate-400 font-normal">NSE:GAIL · Yahoo Finance</span>
            </div>
            {share?.latest && (
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900">₹{share.latest.close}</div>
                {share.change != null && (
                  <div className={`text-xs font-medium flex items-center gap-1 justify-end ${up ? "text-emerald-600" : "text-red-600"}`}>
                    {up ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}
                    {up ? "+" : ""}{share.change} ({share.change_pct}%)
                  </div>
                )}
              </div>
            )}
          </div>
          {share?.chart?.data?.length ? (
            <div className="mt-3"><ChartCard chart={share.chart} index={0} /></div>
          ) : (
            <div className="text-sm text-slate-400 py-8 text-center">Loading live market data…</div>
          )}
        </div>

        <div className="card p-5" data-testid="dashboard-news-card">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3">
            <Newspaper size={18} weight="duotone" className="text-[#be185d]" /> Latest GAIL News <span className="text-[11px] text-slate-400 font-normal">MoneyControl</span>
          </div>
          <div className="space-y-1">
            {news.map((n, i) => (
              <a key={i} data-testid={`news-item-${i}`} href={n.url} target="_blank" rel="noreferrer"
                className="flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-slate-50 group">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#be185d] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 group-hover:text-[#004182] leading-snug font-medium line-clamp-2">{n.title}</div>
                  {n.summary && <div className="text-[12px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">{n.summary}</div>}
                  {n.date && <div className="text-[11px] text-slate-400 mt-0.5">{n.date}</div>}
                </div>
                <ArrowSquareOut size={14} className="text-slate-300 group-hover:text-[#004182] shrink-0 mt-0.5" />
              </a>
            ))}
            {news.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">Loading latest news…</div>}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl font-bold text-slate-900">Previously Generated Reports</h2>
          <button className="text-sm font-medium text-[#004182] hover:underline" data-testid="view-all-reports"
            onClick={() => navigate("/history")}>View all</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {(stats?.recent_reports || []).map((r) => (
            <div key={r.id} data-testid={`recent-report-${r.id}`}
              className="card card-hover p-6 cursor-pointer group" onClick={() => navigate(`/reports/${r.id}`)}>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="tag-purple text-[11px] font-medium">Issue {r.issue_no}</span>
                <span>{r.issue_date}</span>
              </div>
              <div className="text-slate-900 font-semibold text-lg mt-4 leading-snug line-clamp-3">{r.title}</div>
              <div className="flex items-center justify-between mt-5">
                <StatusPill status={r.status} />
                <ArrowRight size={20} className="text-slate-300 group-hover:text-[#004182] transition-colors" />
              </div>
            </div>
          ))}
          {stats && stats.recent_reports.length === 0 && (
            <div className="text-slate-500 text-sm">No reports yet. Generate your first report.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    generating: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`text-[11px] px-2.5 py-1 rounded-full border capitalize font-medium ${map[status] || map.completed}`}>{status}</span>;
}
